/*
PURPOSE: Reusable component for rendering user names in gesture contexts

USE CASES:
- Display user as "You" when they're the current user
- Render user as "you" (lowercase) when appropriate
- Make names clickable links to user profiles
- Apply bold styling to emphasize the "other party"
- Handle null/missing user data gracefully

DOMAIN BOUNDARIES:
- Handles user name rendering in gesture UI
- Manages navigation to user profiles
- Applies perspective-based styling
- Does NOT handle gesture logic or data fetching

ASSUMPTIONS:
- User IDs are unique
- Navigation paths follow /u/{userId}?view=gestures pattern
- Bolding is determined by role and perspective
*/

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { isCurrentUser, shouldBoldName, UserPerspective } from '../../utils/gestureUtils';

interface GestureUserLinkProps {
  userId: string | undefined;
  displayName: string;
  currentUserId: string | undefined;
  role: 'sender' | 'target';
  perspective: UserPerspective;
  capitalizeYou?: boolean; // true for "You", false for "you"
}

const GestureUserLink: React.FC<GestureUserLinkProps> = ({
  userId,
  displayName,
  currentUserId,
  role,
  perspective,
  capitalizeYou = true
}) => {
  const navigate = useNavigate();

  // Determine styling
  const isBold = shouldBoldName(role, perspective);
  const fontClass = isBold ? 'font-semibold' : 'font-normal';

  // Handle "You" case
  if (isCurrentUser(userId, currentUserId)) {
    const youText = capitalizeYou ? 'You' : 'you';
    return <span className="font-normal">{youText}</span>;
  }

  // Handle clickable name
  if (userId) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/u/${userId}?view=gestures`);
        }}
        className={`hover:underline ${fontClass}`}
      >
        {displayName}
      </button>
    );
  }

  // Fallback for missing user ID
  return <span className={fontClass}>{displayName}</span>;
};

export default GestureUserLink;
